// Service Steady PWA — src/lib/api/weather.ts — v3.0 — built 2026-06-04
//
// Weather + location-search API client (v3 — Open-Meteo primary).
// Worker endpoints:
//   GET /pwa/weather                    — compact card payload
//   GET /pwa/weather/detail             — full detail (modal)
//   GET /pwa/weather/search?q=          — geocoding
//
// Worker returns HTTP 200 either way: { ok:true, ... } on success or
// { ok:false, reason } on graceful degrade.

import { api } from '../api';
import type { WeatherCategory } from '../weatherStyle';

// Re-export so consumers can grab WeatherCategory from this module too —
// keeps imports tidy in WeatherCard / WeatherDetailModal.
export type { WeatherCategory };

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export interface WeatherLocation {
  name: string;
  lat: number;
  lon: number;
}

export interface WeatherCurrent {
  temp_f: number;
  feels_like_f: number;
  condition_short: string;
  weather_code: number;
  category: WeatherCategory;
  is_day: boolean;
  humidity_pct: number;
  wind_mph: number;
  wind_direction: string;
}

export interface WeatherTodayCard {
  precip_chance_pct: number;
  high_f: number | null;
  low_f: number | null;
}

// ---------------------------------------------------------------------------
// Card payload (compact)
// ---------------------------------------------------------------------------

export type WeatherCardPayload =
  | {
      ok: true;
      location: WeatherLocation;
      current: WeatherCurrent;
      today: WeatherTodayCard;
      /** v3.1 — short-range precip nowcast. Null when no transition in
       *  the next 2 hours. Examples:
       *    "Rain starting around 11:15 am"
       *    "Snow stopping around 9:45 am" */
      nowcast: string | null;
      source: 'open-meteo';
      generated_at: string;
      cached: boolean;
    }
  | { ok: false; reason: string };

// ---------------------------------------------------------------------------
// Detail payload (modal)
// ---------------------------------------------------------------------------

export interface WeatherHourly {
  time: string; // ISO local
  temp_f: number;
  precip_chance_pct: number;
  weather_code: number;
  category: WeatherCategory;
  is_day: boolean;
}

export interface WeatherDailyEntry {
  date: string; // YYYY-MM-DD
  day_name: string; // 'Today' / 'Tomorrow' / weekday
  high_f: number | null;
  low_f: number | null;
  precip_chance_pct: number;
  weather_code: number;
  category: WeatherCategory;
  condition_short: string;
  // v3.2 per-day fields (drill-down panel)
  sunrise: string | null;
  sunset: string | null;
  uv_index: number | null;
  precip_inches: number | null;
  wind_max_mph: number | null;
}

export interface WeatherAirQuality {
  us_aqi: number | null;
  category:
    | 'good'
    | 'moderate'
    | 'usg'
    | 'unhealthy'
    | 'very_unhealthy'
    | 'hazardous'
    | 'unknown';
  pm2_5: number | null;
  pm10: number | null;
  o3: number | null;
  no2: number | null;
}

export type WeatherDetailPayload =
  | {
      ok: true;
      location: WeatherLocation;
      current: WeatherCurrent & { wind_gusts_mph: number };
      today: {
        precip_chance_pct: number;
        high_f: number | null;
        low_f: number | null;
        sunrise: string | null;
        sunset: string | null;
        uv_index: number | null;
        precip_inches: number | null;
      };
      hourly_today: WeatherHourly[];
      daily: WeatherDailyEntry[];
      /** v3.2 — full hourly bucketed by local YYYY-MM-DD. Used by the
       *  per-day drill-down panel in WeatherDetailModal. */
      hourly_by_day: Record<string, WeatherHourly[]>;
      air_quality: WeatherAirQuality | null;
      source: 'open-meteo';
      generated_at: string;
      cached: boolean;
    }
  | { ok: false; reason: string };

// ---------------------------------------------------------------------------
// Search payload
// ---------------------------------------------------------------------------

export interface LocationSearchHit {
  name: string;
  lat: number;
  lon: number;
  county: string;
}

// ---------------------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------------------

function buildLocPath(
  base: string,
  loc?: { lat: number; lon: number; name?: string },
): string {
  if (!loc) return base;
  const params = new URLSearchParams({
    lat: loc.lat.toFixed(5),
    lon: loc.lon.toFixed(5),
  });
  if (loc.name) params.set('name', loc.name);
  return `${base}?${params.toString()}`;
}

export async function fetchWeather(
  loc?: { lat: number; lon: number; name?: string },
): Promise<WeatherCardPayload> {
  return api.request<WeatherCardPayload>(buildLocPath('/pwa/weather', loc));
}

export async function fetchWeatherDetail(
  loc?: { lat: number; lon: number; name?: string },
): Promise<WeatherDetailPayload> {
  return api.request<WeatherDetailPayload>(buildLocPath('/pwa/weather/detail', loc));
}

export async function fetchLocationSearch(q: string): Promise<LocationSearchHit[]> {
  const trimmed = q.trim();
  if (trimmed.length < 2) return [];
  const path = `/pwa/weather/search?q=${encodeURIComponent(trimmed)}`;
  return api.request<LocationSearchHit[]>(path);
}
